import { useEffect, useRef, useCallback } from "react";

export interface KeyboardShortcutConfig {
  key: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  metaKey?: boolean;
  preventDefault?: boolean;
  stopPropagation?: boolean;
}

export interface UseKeyboardShortcutOptions {
  enabled?: boolean;
  target?: HTMLElement | Document | null;
  priority?: number; // Higher priority shortcuts will be handled first
  description?: string; // For debugging/documentation
}

interface ShortcutHandler {
  config: KeyboardShortcutConfig;
  handler: (event: KeyboardEvent) => void;
  options: UseKeyboardShortcutOptions;
  id: string;
}

// Global registry to handle conflicts and priority
const shortcutRegistry = new Map<string, ShortcutHandler[]>();
let nextId = 0;

function generateShortcutKey(config: KeyboardShortcutConfig): string {
  const modifiers = [];
  if (config.ctrlKey) modifiers.push("ctrl");
  if (config.altKey) modifiers.push("alt");
  if (config.shiftKey) modifiers.push("shift");
  if (config.metaKey) modifiers.push("meta");

  return `${modifiers.join("+")}${modifiers.length ? "+" : ""}${config.key.toLowerCase()}`;
}

function matchesShortcut(
  event: KeyboardEvent,
  config: KeyboardShortcutConfig,
): boolean {
  return (
    event.key.toLowerCase() === config.key.toLowerCase() &&
    !!event.ctrlKey === !!config.ctrlKey &&
    !!event.altKey === !!config.altKey &&
    !!event.shiftKey === !!config.shiftKey &&
    !!event.metaKey === !!config.metaKey
  );
}

function handleGlobalKeydown(event: KeyboardEvent) {
  const targetHandlers: ShortcutHandler[] = [];

  // Collect all matching handlers
  shortcutRegistry.forEach((handlers) => {
    handlers.forEach((handler) => {
      if (
        handler.options.enabled !== false &&
        matchesShortcut(event, handler.config)
      ) {
        // Check if handler's target matches the event target or contains it
        const target = handler.options.target || document;
        const eventTarget = event.target as Element;

        if (
          target === document ||
          target === eventTarget ||
          (target instanceof Element && target.contains(eventTarget))
        ) {
          targetHandlers.push(handler);
        }
      }
    });
  });

  if (targetHandlers.length === 0) return;

  // Sort by priority (higher priority first)
  targetHandlers.sort(
    (a, b) => (b.options.priority || 0) - (a.options.priority || 0),
  );

  // Execute the highest priority handler
  const handler = targetHandlers[0];

  if (handler.config.preventDefault !== false) {
    event.preventDefault();
  }

  if (handler.config.stopPropagation !== false) {
    event.stopPropagation();
  }

  // Log conflicts for debugging
  if (targetHandlers.length > 1 && process.env.NODE_ENV === "development") {
    console.warn("Keyboard shortcut conflict detected:", {
      key: generateShortcutKey(handler.config),
      handlers: targetHandlers.map((h) => ({
        id: h.id,
        priority: h.options.priority || 0,
        description: h.options.description,
      })),
      executed: handler.id,
    });
  }

  handler.handler(event);
}

// Setup global listener once
let globalListenerSetup = false;

function setupGlobalListener() {
  if (!globalListenerSetup) {
    document.addEventListener("keydown", handleGlobalKeydown, true);
    globalListenerSetup = true;
  }
}

/**
 * Hook for registering keyboard shortcuts with conflict resolution
 *
 * Features:
 * - Automatic conflict resolution using priority system
 * - Support for nested components
 * - Target-specific shortcuts (element, document)
 * - Enable/disable functionality
 * - Development mode conflict warnings
 *
 * @param config - Keyboard shortcut configuration
 * @param handler - Function to execute when shortcut is triggered
 * @param options - Additional options for the shortcut
 *
 * @example
 * // Simple shortcut
 * useKeyboardShortcut(
 *   { key: 's', ctrlKey: true },
 *   () => console.log('Save shortcut triggered')
 * );
 *
 * @example
 * // High priority shortcut that overrides others
 * useKeyboardShortcut(
 *   { key: 'Escape' },
 *   () => closeModal(),
 *   { priority: 100, description: 'Close modal' }
 * );
 *
 * @example
 * // Element-specific shortcut
 * const inputRef = useRef<HTMLInputElement>(null);
 * useKeyboardShortcut(
 *   { key: 'Enter' },
 *   () => submitForm(),
 *   { target: inputRef.current, description: 'Submit form' }
 * );
 */
export function useKeyboardShortcut(
  config: KeyboardShortcutConfig,
  handler: (event: KeyboardEvent) => void,
  options: UseKeyboardShortcutOptions = {},
) {
  const handlerRef = useRef(handler);
  const configRef = useRef(config);
  const optionsRef = useRef(options);
  const idRef = useRef<string>(`shortcut-${nextId++}`);

  // Update refs when dependencies change
  handlerRef.current = handler;
  configRef.current = config;
  optionsRef.current = options;

  const registerShortcut = useCallback(() => {
    const shortcutKey = generateShortcutKey(configRef.current);
    const shortcutHandler: ShortcutHandler = {
      config: configRef.current,
      handler: (event: KeyboardEvent) => handlerRef.current(event),
      options: optionsRef.current,
      id: idRef.current,
    };

    if (!shortcutRegistry.has(shortcutKey)) {
      shortcutRegistry.set(shortcutKey, []);
    }

    const handlers = shortcutRegistry.get(shortcutKey)!;

    // Remove existing handler with same id (for updates)
    const existingIndex = handlers.findIndex((h) => h.id === idRef.current);
    if (existingIndex >= 0) {
      handlers.splice(existingIndex, 1);
    }

    handlers.push(shortcutHandler);

    setupGlobalListener();

    return () => {
      const currentHandlers = shortcutRegistry.get(shortcutKey);
      if (currentHandlers) {
        const index = currentHandlers.findIndex((h) => h.id === idRef.current);
        if (index >= 0) {
          currentHandlers.splice(index, 1);
          if (currentHandlers.length === 0) {
            shortcutRegistry.delete(shortcutKey);
          }
        }
      }
    };
  }, []);

  useEffect(() => {
    // Short-circuit if explicitly disabled
    if (options.enabled === false) {
      return;
    }

    const cleanup = registerShortcut();
    return cleanup;
  }, [
    registerShortcut,
    config.key,
    config.ctrlKey,
    config.altKey,
    config.shiftKey,
    config.metaKey,
    options.enabled,
    options.target,
    options.priority,
  ]);

  // Return utility functions
  return {
    id: idRef.current,
    shortcutKey: generateShortcutKey(config),
    isRegistered: () => {
      const key = generateShortcutKey(config);
      const handlers = shortcutRegistry.get(key);
      return handlers?.some((h) => h.id === idRef.current) || false;
    },
    getConflicts: () => {
      const key = generateShortcutKey(config);
      const handlers = shortcutRegistry.get(key) || [];
      return handlers
        .filter((h) => h.id !== idRef.current)
        .map((h) => ({
          id: h.id,
          priority: h.options.priority || 0,
          description: h.options.description,
        }));
    },
  };
}

/**
 * Hook for registering multiple keyboard shortcuts at once
 *
 * @param shortcuts - Array of shortcut configurations
 *
 * @example
 * useKeyboardShortcuts([
 *   {
 *     config: { key: 's', ctrlKey: true },
 *     handler: () => save(),
 *     options: { description: 'Save document' }
 *   },
 *   {
 *     config: { key: 'z', ctrlKey: true },
 *     handler: () => undo(),
 *     options: { description: 'Undo action' }
 *   }
 * ]);
 */
export function useKeyboardShortcuts(
  shortcuts: Array<{
    config: KeyboardShortcutConfig;
    handler: (event: KeyboardEvent) => void;
    options?: UseKeyboardShortcutOptions;
  }>,
) {
  const results = shortcuts.map(({ config, handler, options }) =>
    useKeyboardShortcut(config, handler, options),
  );

  return {
    shortcuts: results,
    getAllConflicts: () => {
      const allConflicts: Record<string, any[]> = {};
      results.forEach((result) => {
        const conflicts = result.getConflicts();
        if (conflicts.length > 0) {
          allConflicts[result.shortcutKey] = conflicts;
        }
      });
      return allConflicts;
    },
  };
}

// Development helper to inspect all registered shortcuts
export function getRegisteredShortcuts() {
  if (process.env.NODE_ENV === "development") {
    const shortcuts: Record<string, any[]> = {};
    shortcutRegistry.forEach((handlers, key) => {
      shortcuts[key] = handlers.map((h) => ({
        id: h.id,
        priority: h.options.priority || 0,
        description: h.options.description,
        enabled: h.options.enabled !== false,
      }));
    });
    return shortcuts;
  }
  return {};
}
